/**
 * The class strings of Encre's chrome (PLAN-BUREAU § 4 quater): the sidebar's rows and section
 * titles, the icon tools, the menus and popovers. The cards' buttons live in `buttons.ts`. Shared so
 * the sidebar, the menus and the pages speak the same language (rows of 36 px, radius 10, one
 * hover tone, one selected tone, the focus ring in the focus blue only).
 */
const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus';

/** A row of the sidebar or of a menu: 36 px, text 14, the hover tone. */
export const SIDE_ROW = `flex h-9 w-full min-w-0 items-center gap-2.5 rounded-[10px] px-2.5 text-left text-sm text-desk-ink transition-colors hover:bg-desk-hover ${FOCUS}`;

/** The selected row (the open object, the current page): a tint of the blue. */
export const SIDE_ROW_ON = 'bg-desk-accent-soft hover:bg-desk-accent-soft';

/** A section's title in the sidebar (T'attend, Otto travaille, Reporté…): 12 px, medium, muted. */
export const SIDE_SEC = `flex h-8 w-full min-w-0 items-center gap-1.5 rounded-[10px] px-2.5 text-left text-xs font-medium text-desk-muted transition-colors hover:bg-desk-hover ${FOCUS}`;

/** A square icon button (notifications, fold, filters): 36 px, or 28 px with `TOOL_SM`. */
export const TOOL = `relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-desk-soft transition-colors hover:bg-desk-hover hover:text-desk-ink ${FOCUS}`;
export const TOOL_SM = `relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-desk-muted transition-colors hover:bg-desk-hover hover:text-desk-ink ${FOCUS}`;
/** A tool whose panel is open, or whose filter is set. */
export const TOOL_ON = 'bg-desk-accent-soft text-desk-accent-text';

/** A floating menu or popover: card, radius 16, the pop shadow. */
export const POP = 'z-40 rounded-2xl bg-desk-card p-1.5 text-desk-ink shadow-pop focus:outline-none';
/** An item of a menu: 36 px at least, text 14. */
export const MENU_ITEM = `flex min-h-9 w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left text-sm text-desk-ink transition-colors hover:bg-desk-hover disabled:cursor-not-allowed disabled:opacity-40 ${FOCUS}`;
/** The small label over a group of items (« Reporter », « Voie »). */
export const MENU_LABEL = 'px-2.5 pb-1 pt-2 text-xs font-medium text-desk-muted';
/** The thin rule between groups of items. */
export const MENU_RULE = '-mx-1.5 my-1.5 h-px border-0 bg-desk-line';

/** A segmented choice (Clair · Sombre · Système, Tout · Otto · Toi). */
export const SEG = 'inline-flex gap-0.5 rounded-full p-0.5 ring-1 ring-inset ring-desk-ring';
export const SEG_ITEM = `h-8 rounded-full px-3.5 text-sm font-medium text-desk-soft transition-colors aria-pressed:bg-desk-accent-soft aria-pressed:text-desk-accent-text ${FOCUS}`;

/** A field (search in a list, a reason to type). */
export const FIELD =
  'h-9 w-full rounded-[10px] bg-desk-bg px-2.5 text-sm text-desk-ink ring-1 ring-inset ring-desk-ring placeholder:text-desk-muted focus:outline-none focus:ring-2 focus:ring-desk-focus';

/**
 * An icon in its blue pastille (a row's nature, an exchange, a mission): Encre with AutoQuote's
 * blue — « c'est un encre avec du bleu, notre design system » (Gilles, 13/10/2026).
 */
export const ICON_PASTILLE = 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-desk-accent-soft text-desk-accent-text';

/** A name that leads somewhere, in a sentence (mock-up « Liens »): underlined in light blue, blue when hovered. */
export const REF = `rounded-[4px] text-left text-inherit underline decoration-desk-accent/40 decoration-1 underline-offset-4 transition-colors hover:bg-desk-accent-soft hover:text-desk-accent-text hover:decoration-desk-accent ${FOCUS}`;

/** A line that opens something in a block (a sheet's, a record's): the full width, its hover past the text. */
export const BLOCK_ROW = `-mx-2.5 flex w-[calc(100%+1.25rem)] min-w-0 items-center gap-3 rounded-[10px] px-2.5 py-2 text-left transition-colors hover:bg-desk-hover ${FOCUS}`;

/** A status in a small pill; its colour from `TONE`. */
export const CHIP = 'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium';

/** A chip's tones: soft semantic colours, each with its dark variant; neutral and accent from the tokens. */
export const TONE = {
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  danger: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  neutral: 'bg-desk-subtle text-desk-soft',
  accent: 'bg-desk-accent-soft text-desk-accent-text',
} as const;

/** A block's gesture said in words, on the right of its title (« Gérer », « Ajouter »). */
export const BLOCK_ACTION = `rounded-[4px] text-sm font-medium text-desk-accent-text hover:underline ${FOCUS}`;
